/**
 * FlowView Component
 *
 * Displays documents grouped by time period with infinite scroll pagination.
 * Groups: Today, Yesterday, This Week, This Month, Older
 *
 * @module features/dashboard/components/FlowView
 * @see SPEC-016 Requirements 5.1, 5.2, 5.3, 5.4
 * @see SPEC-017 Task 6.2 - i18n integration
 */

import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { cn } from '../../../lib/cn';
import { useTypedTranslation } from '../../../i18n';
import { formatDateTime } from '../../../i18n/formatters';
import type { SupportedDateLocale } from '../../../i18n/formatters';
import type { DocListItemInput } from '@linkode/shared';
import { DocCard } from './DocCard';
import { groupByTimePeriod, getRelativeTime, type TimeGroup } from '../utils/groupByTimePeriod';

// ============================================================================
// Types
// ============================================================================

export interface FlowViewProps {
  /** Documents to display */
  docs: DocListItemInput[];
  /** Selected document IDs */
  selectedDocIds: string[];
  /** Whether there are more documents to load */
  hasMore: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Document click handler */
  onDocClick: (doc: DocListItemInput) => void;
  /** Document context menu handler */
  onDocContextMenu: (doc: DocListItemInput, event: React.MouseEvent) => void;
  /** Document selection toggle handler */
  onDocSelect: (docId: string) => void;
  /** Load more documents handler (infinite scroll) */
  onLoadMore: () => void;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/** Threshold in pixels from bottom to trigger load more */
const LOAD_MORE_THRESHOLD = 200;

/** Time in ms to consider a document as "recently edited" */
const RECENTLY_EDITED_THRESHOLD = 5 * 60 * 1000; // 5 minutes

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get locale from i18n language code
 */
function getLocaleFromLanguage(language: string): SupportedDateLocale {
  if (language === 'zh-TW' || language.startsWith('zh')) {
    return 'zh-TW';
  }
  return 'en';
}

// ============================================================================
// Helper Components
// ============================================================================

interface TimeGroupHeaderProps {
  label: string;
  count: number;
}

const TimeGroupHeader: React.FC<TimeGroupHeaderProps> = ({ label, count }) => (
  <div className="flex items-center gap-3 mb-4">
    <h3 className="font-mono font-bold text-lg text-linkode-text">{label}</h3>
    {/* eslint-disable-next-line react/jsx-no-literals -- Parentheses are symbols */}
    <span className="text-sm text-linkode-text/50 font-mono">({count})</span>
    <div className="flex-1 h-px bg-linkode-text/20" />
  </div>
);

interface FlowDocCardProps {
  doc: DocListItemInput;
  isSelected: boolean;
  isRecentlyEdited: boolean;
  locale: SupportedDateLocale;
  editingLabel: string;
  recentlyEditedLabel: string;
  onClick: (doc: DocListItemInput) => void;
  onContextMenu: (doc: DocListItemInput, event: React.MouseEvent) => void;
  onSelect: (docId: string) => void;
}

const FlowDocCard: React.FC<FlowDocCardProps> = ({
  doc,
  isSelected,
  isRecentlyEdited,
  locale,
  editingLabel,
  recentlyEditedLabel,
  onClick,
  onContextMenu,
  onSelect,
}) => {
  const relativeTime = useMemo(
    () => getRelativeTime(doc.updatedAt, locale),
    [doc.updatedAt, locale]
  );

  return (
    <div className="relative group">
      {/* Recently Edited Indicator */}
      {isRecentlyEdited && (
        <div
          className={cn(
            'absolute -left-3 top-1/2 -translate-y-1/2',
            'w-2 h-2 rounded-full',
            'bg-linkode-teal',
            'animate-pulse'
          )}
          title={recentlyEditedLabel}
          aria-label={recentlyEditedLabel}
        />
      )}

      <div className="flex items-start gap-4">
        {/* Timeline Dot */}
        <div className="flex flex-col items-center pt-4">
          <div
            className={cn(
              'w-3 h-3 rounded-full border-2',
              isRecentlyEdited
                ? 'border-linkode-teal bg-linkode-teal/20'
                : 'border-linkode-text/30 bg-white'
            )}
          />
          <div className="w-px flex-1 bg-linkode-text/10 min-h-[20px]" />
        </div>

        {/* Card Content */}
        <div className="flex-1 pb-4">
          <DocCard
            doc={doc}
            selected={isSelected}
            onClick={onClick}
            onContextMenu={onContextMenu}
          />

          {/* Relative Time */}
          <div className="mt-2 flex items-center gap-2">
            <time
              className="text-xs text-linkode-text/50 font-mono"
              dateTime={new Date(doc.updatedAt).toISOString()}
              title={formatDateTime(new Date(doc.updatedAt), locale)}
            >
              {relativeTime}
            </time>

            {/* Activity Indicator */}
            {isRecentlyEdited && (
              <span className="text-xs text-linkode-teal font-mono">• {editingLabel}</span>
            )}
          </div>
        </div>

        {/* Selection Checkbox */}
        <button
          type="button"
          className={cn(
            'mt-4 p-1.5 rounded-linkode',
            'opacity-0 group-hover:opacity-100',
            'transition-opacity duration-150',
            'text-linkode-text/50 hover:text-linkode-text',
            'hover:bg-linkode-bg-light',
            'focus:outline-none focus:ring-2 focus:ring-linkode-teal focus:opacity-100'
          )}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(doc.docId);
          }}
          aria-label={isSelected ? 'Deselect document' : 'Select document'}
        >
          {isSelected ? (
            <svg className="w-5 h-5 text-linkode-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

/**
 * FlowView Component
 *
 * Features:
 * - Documents grouped by time period (Today, Yesterday, This Week, This Month, Older)
 * - Infinite scroll pagination
 * - Recently edited indicator (pulse animation)
 * - Timeline-style layout
 * - Relative timestamps
 *
 * @example
 * ```tsx
 * <FlowView
 *   docs={documents}
 *   selectedDocIds={[]}
 *   hasMore={true}
 *   isLoading={false}
 *   onDocClick={(doc) => navigate(`/editor/${doc.docId}`)}
 *   onDocContextMenu={(doc, e) => showContextMenu(e, doc)}
 *   onDocSelect={(docId) => toggleSelection(docId)}
 *   onLoadMore={() => loadMoreDocs()}
 * />
 * ```
 *
 * @see Requirements 5.1, 5.2, 5.3, 5.4
 */
export const FlowView: React.FC<FlowViewProps> = ({
  docs,
  selectedDocIds,
  hasMore,
  isLoading,
  onDocClick,
  onDocContextMenu,
  onDocSelect,
  onLoadMore,
  className,
}) => {
  const { t, i18n } = useTypedTranslation();
  const locale = getLocaleFromLanguage(i18n.language);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Get translated labels
  const editingLabel = t('dashboard.flowView.editing');
  const recentlyEditedLabel = t('dashboard.flowView.recentlyEdited');
  const loadingMoreLabel = t('dashboard.flowView.loadingMore');
  const endOfDocumentsLabel = t('dashboard.flowView.endOfDocuments');

  // Get translated time period labels
  const timePeriodLabels = useMemo(
    () => ({
      today: t('dashboard.flowView.groups.today'),
      yesterday: t('dashboard.flowView.groups.yesterday'),
      thisWeek: t('dashboard.flowView.groups.thisWeek'),
      thisMonth: t('dashboard.flowView.groups.thisMonth'),
      older: t('dashboard.flowView.groups.older'),
    }),
    [t]
  );

  // Group documents by time period and apply translated labels
  const timeGroups = useMemo<TimeGroup<DocListItemInput>[]>(() => {
    const groups = groupByTimePeriod(docs);
    // Override labels with translated versions
    return groups.map((group) => ({
      ...group,
      label: timePeriodLabels[group.period],
    }));
  }, [docs, timePeriodLabels]);

  // Check if a document was recently edited
  const isRecentlyEdited = useCallback((doc: DocListItemInput): boolean => {
    return Date.now() - doc.updatedAt < RECENTLY_EDITED_THRESHOLD;
  }, []);

  // Infinite scroll using Intersection Observer
  useEffect(() => {
    if (!hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: `${LOAD_MORE_THRESHOLD}px`,
        threshold: 0,
      }
    );

    const loadMoreElement = loadMoreRef.current;
    if (loadMoreElement) {
      observer.observe(loadMoreElement);
    }

    return () => {
      if (loadMoreElement) {
        observer.unobserve(loadMoreElement);
      }
    };
  }, [hasMore, isLoading, onLoadMore]);

  return (
    <div
      ref={containerRef}
      className={cn('space-y-8', className)}
      role="feed"
      aria-label="Document timeline"
      aria-busy={isLoading}
    >
      {/* Time Groups */}
      {timeGroups.map((group) => (
        <section key={group.period} aria-labelledby={`group-${group.period}`}>
          <TimeGroupHeader label={group.label} count={group.docs.length} />

          <div className="space-y-2 pl-4">
            {group.docs.map((doc) => {
              if (!doc) return null;
              return (
                <FlowDocCard
                  key={doc.docId}
                  doc={doc}
                  isSelected={selectedDocIds.includes(doc.docId)}
                  isRecentlyEdited={isRecentlyEdited(doc)}
                  locale={locale}
                  editingLabel={editingLabel}
                  recentlyEditedLabel={recentlyEditedLabel}
                  onClick={onDocClick}
                  onContextMenu={onDocContextMenu}
                  onSelect={onDocSelect}
                />
              );
            })}
          </div>
        </section>
      ))}

      {/* Load More Trigger */}
      <div ref={loadMoreRef} className="h-1" aria-hidden="true" />

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3 text-linkode-text/50">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-linkode-text/30 border-t-linkode-text" />
            <span className="font-mono text-sm">{loadingMoreLabel}</span>
          </div>
        </div>
      )}

      {/* End of List */}
      {!hasMore && docs.length > 0 && (
        <div className="text-center py-8">
          <span className="text-sm text-linkode-text/40 font-mono">{endOfDocumentsLabel}</span>
        </div>
      )}
    </div>
  );
};
